import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, X } from "lucide-react";
import { artistes } from "@/data/artistes";
import { useLang, usePageMeta } from "@/App";
import { Reveal } from "@/components/Section";

const LABELS = {
  fr: { back: "VIVA ARTIST — la famille des artistes", visit: "Visiter le site de l'artiste", next: "Artiste suivant", close: "Fermer" },
  en: { back: "VIVA ARTIST — the family of artists", visit: "Visit the artist's website", next: "Next artist", close: "Close" },
  es: { back: "VIVA ARTIST — la familia de artistas", visit: "Visitar el sitio del artista", next: "Artista siguiente", close: "Cerrar" },
  it: { back: "VIVA ARTIST — la famiglia degli artisti", visit: "Visitare il sito dell'artista", next: "Artista successivo", close: "Chiudere" },
  pt: { back: "VIVA ARTIST — a família dos artistas", visit: "Visitar o site do artista", next: "Artista seguinte", close: "Fechar" },
  de: { back: "VIVA ARTIST — die Künstlerfamilie", visit: "Website des Künstlers besuchen", next: "Nächster Künstler", close: "Schließen" },
  nl: { back: "VIVA ARTIST — de kunstenaarsfamilie", visit: "Website van de kunstenaar bezoeken", next: "Volgende kunstenaar", close: "Sluiten" },
  pl: { back: "VIVA ARTIST — rodzina artystów", visit: "Odwiedź stronę artysty", next: "Następny artysta", close: "Zamknij" },
  ro: { back: "VIVA ARTIST — familia artiștilor", visit: "Vizitează site-ul artistului", next: "Artistul următor", close: "Închide" },
  cs: { back: "VIVA ARTIST — rodina umělců", visit: "Navštívit web umělce", next: "Další umělec", close: "Zavřít" },
  sv: { back: "VIVA ARTIST — konstnärsfamiljen", visit: "Besök konstnärens webbplats", next: "Nästa konstnär", close: "Stäng" },
  da: { back: "VIVA ARTIST — kunstnerfamilien", visit: "Besøg kunstnerens hjemmeside", next: "Næste kunstner", close: "Luk" },
  fi: { back: "VIVA ARTIST — taiteilijaperhe", visit: "Vieraile taiteilijan sivustolla", next: "Seuraava taiteilija", close: "Sulje" },
  el: { back: "VIVA ARTIST — η οικογένεια των καλλιτεχνών", visit: "Επισκεφθείτε τον ιστότοπο του καλλιτέχνη", next: "Επόμενος καλλιτέχνης", close: "Κλείσιμο" },
  tr: { back: "VIVA ARTIST — sanatçı ailesi", visit: "Sanatçının sitesini ziyaret edin", next: "Sonraki sanatçı", close: "Kapat" },
  uk: { back: "VIVA ARTIST — родина митців", visit: "Відвідати сайт митця", next: "Наступний митець", close: "Закрити" },
  ru: { back: "VIVA ARTIST — семья художников", visit: "Посетить сайт художника", next: "Следующий художник", close: "Закрыть" },
  ar: { back: "VIVA ARTIST — عائلة الفنانين", visit: "زيارة موقع الفنان", next: "الفنان التالي", close: "إغلاق" },
  ja: { back: "VIVA ARTIST——芸術家の家族", visit: "アーティストのサイトを訪れる", next: "次のアーティスト", close: "閉じる" },
  zh: { back: "VIVA ARTIST——艺术家之家", visit: "访问艺术家官网", next: "下一位艺术家", close: "关闭" },
};

const ArtistePage = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const L = LABELS[lang] || LABELS.fr;
  const [lightbox, setLightbox] = useState(null);

  const index = artistes.findIndex((a) => a.slug === slug);
  const artist = index !== -1 ? artistes[index] : null;
  usePageMeta(
    artist ? `${artist.name} — VIVA ARTIST | Gilles de Chassey` : "VIVA ARTIST | Gilles de Chassey",
    artist ? `${artist.name}, ${artist.role[lang] || artist.role.fr} — ${artist.linkLabel}. Le label tous médias du Prieuré Saint-Léonard.` : "Le label tous médias du Prieuré Saint-Léonard.",
    artist ? `/artiste/${artist.slug}` : "/"
  );
  if (!artist) return <Navigate to="/" replace />;
  const next = artistes[(index + 1) % artistes.length];
  const bio = artist.bio[lang] || artist.bio.fr;
  const hasImages = artist.works.some((w) => w.img);

  return (
    <div className="min-h-screen bg-paper" data-testid={`artiste-page-${artist.slug}`}>
      <div className="pt-28 md:pt-36 px-5 md:px-10 pb-24">
        <div className="max-w-[1200px] mx-auto">
          <Reveal>
            <Link to="/" data-testid="artiste-back-link" className="inline-flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-muted2 hover:text-terra transition-colors mb-14">
              <ArrowLeft size={14} /> {L.back}
            </Link>
          </Reveal>

          <Reveal delay={0.05}>
            <p className="font-mono2 text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-terra mb-5">{L.back.split("—")[0]}</p>
            <h1 data-testid="artiste-name" className="font-display font-light text-5xl sm:text-6xl lg:text-7xl text-ink leading-[1.02] tracking-tight">
              {artist.name}
            </h1>
            <p data-testid="artiste-role" className="font-display italic text-2xl md:text-3xl text-bronze mt-4">
              {artist.role[lang] || artist.role.fr}
            </p>
            <a
              data-testid="artiste-site-link"
              href={artist.link}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-2.5 mt-8 border border-ink text-ink hover:bg-ink hover:text-paper transition-colors px-6 py-3 font-mono2 text-[10px] uppercase tracking-[0.2em]"
            >
              {L.visit} — {artist.linkLabel}
              <ArrowUpRight size={13} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </Reveal>

          <Reveal delay={0.1} className="mt-14">
            <div className="border-l-2 border-terra pl-6 md:pl-10 max-w-3xl space-y-6" data-testid="artiste-bio">
              {bio.map((p, i) => (
                <p key={i} className={`leading-[1.85] ${i === 0 ? "font-display text-xl md:text-2xl text-ink" : "text-ink2 text-base"}`}>
                  {p}
                </p>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.12} className="mt-16">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-terra mb-8">
              {artist.worksTitle[lang] || artist.worksTitle.fr}
            </p>
            {hasImages ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-5" data-testid="artiste-gallery">
                {artist.works.map((w, i) => (
                  <button
                    key={i}
                    data-testid={`artiste-work-${i}`}
                    onClick={() => setLightbox(w)}
                    className="group relative text-left bg-inkdeep overflow-hidden"
                  >
                    <div className="relative overflow-hidden aspect-[3/4]">
                      <img src={w.img} alt={w.title} loading="lazy" className="w-full h-full object-cover img-lumiere group-hover:scale-[1.05] transition-transform duration-[1200ms] ease-out" />
                      <div className="absolute inset-0 bg-gradient-to-t from-inkdeep/85 via-transparent to-transparent" />
                    </div>
                    <p className="absolute bottom-0 inset-x-0 p-4 font-display text-lg md:text-xl text-paper leading-tight">{w.title}</p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="border-t border-line" data-testid="artiste-timeline">
                {artist.works.map((w, i) => (
                  <div key={i} data-testid={`artiste-work-${i}`} className="group grid grid-cols-[90px_1fr] md:grid-cols-[140px_1fr] gap-4 md:gap-8 py-5 border-b border-line hover:bg-parchment/60 transition-colors px-2 md:px-4 -mx-2 md:-mx-4">
                    <span className="font-mono2 text-[11px] md:text-xs uppercase tracking-[0.15em] text-terra pt-1">{w.year}</span>
                    <div>
                      <h3 className="font-display text-xl md:text-2xl text-ink group-hover:text-terra transition-colors leading-tight">{w.title}</h3>
                      <p className="text-sm text-muted2 mt-1 leading-relaxed">{w.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Reveal>

          {artist.bandcamp && (
            <Reveal delay={0.14} className="mt-16">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-terra mb-6" data-testid="artiste-audio-title">
                {artist.bandcamp.title[lang] || artist.bandcamp.title.fr}
              </p>
              <iframe
                data-testid="artiste-audio-player"
                title="Bandcamp — Renara Akhoundova"
                src={`https://bandcamp.com/EmbeddedPlayer/album=${artist.bandcamp.album}/size=large/bgcol=f5efe4/linkcol=9a5a34/tracklist=false/transparent=true/`}
                seamless
                className="w-full border-0 h-[241px]"
              />
            </Reveal>
          )}

          <Reveal className="mt-16">
            <Link
              data-testid="artiste-next-link"
              to={`/artiste/${next.slug}`}
              className="group inline-flex items-center gap-4 border border-line hover:border-terra transition-colors px-7 py-5"
            >
              <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-muted2">{L.next}</span>
              <span className="font-display text-2xl text-ink group-hover:text-terra transition-colors">{next.name}</span>
              <ArrowRight size={16} className="text-terra group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="artiste-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[90] bg-inkdeep/95 backdrop-blur-md flex items-center justify-center p-5 md:p-12"
            onClick={() => setLightbox(null)}
          >
            <button data-testid="artiste-lightbox-close" onClick={() => setLightbox(null)} className="absolute top-6 right-6 text-paper/70 hover:text-gold transition-colors p-2" aria-label={L.close}>
              <X size={26} />
            </button>
            <motion.figure
              initial={{ scale: 0.94, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 16 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-3xl w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={lightbox.img} alt={lightbox.title} className="max-h-[75vh] w-auto mx-auto border border-linedark" />
              <figcaption className="font-display text-2xl text-paper mt-5">{lightbox.title}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ArtistePage;
